import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-12 px-6 border-t border-[var(--border-subtle)] bg-[var(--bg-deep)]" data-id="element-111">
        <div className="max-w-[680px] mx-auto" data-id="element-112">
          {/* Payment trust badges */}
          <div className="flex items-center justify-center gap-3 mb-8 flex-wrap" data-id="element-113">
            {['Visa', 'Mastercard', 'PayPal', 'Amex', 'Apple Pay'].map(method => <div key={method} className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded px-3 py-1.5 text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider shadow-sm" data-id="element-114">
                  {method}
                </div>)}
          </div>

          <div className="flex justify-center items-center gap-6" data-id="element-115">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer" data-id="element-116">
              Privacy Policy
            </button>
            <span className="text-[var(--border-subtle)]" data-id="element-117">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer" data-id="element-118">
              Terms & Conditions
            </button>
          </div>
          <p className="text-center text-xs text-[var(--text-muted)] mt-6" data-id="element-119">
            © {new Date().getFullYear()} Sensory Feeding Victory. All rights
            reserved.
          </p>
          <p className="text-center text-[10px] text-[var(--text-muted)] opacity-70 mt-2 max-w-md mx-auto" data-id="element-120">
            This is a digital product. No physical product will be shipped. This
            guide is for educational purposes and does not replace professional
            medical advice.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-121">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-122">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} onClick={e => e.stopPropagation()} className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" data-id="element-123">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]" data-id="element-124">
                <h3 className="text-lg font-serif font-bold text-[var(--text-heading)]" data-id="element-125">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-[var(--text-muted)] hover:text-[var(--text-heading)] transition-colors p-1 bg-[var(--bg-card)] rounded-full" data-id="element-126">
                  <X className="w-5 h-5" data-id="element-127" />
                </button>
              </div>

              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-70px)] text-[var(--text-primary)] text-sm leading-relaxed space-y-4" data-id="element-128">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-129" /> : <TermsContent data-id="element-130" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-[var(--text-muted)] text-xs uppercase tracking-widest mb-4" data-id="element-131">
        Last Updated: 2026
      </p>
      <h4 className="font-bold text-[var(--text-heading)] mt-4" data-id="element-132">
        1. Information We Collect
      </h4>
      <p data-id="element-133">
        When you purchase our guide, we collect only the necessary information
        to process your order: email address and payment details. We do not
        store credit card data directly; it is securely processed by our payment
        provider.
      </p>
      <h4 className="font-bold text-[var(--text-heading)] mt-4" data-id="element-134">
        2. How We Use Information
      </h4>
      <p data-id="element-135">
        Your information is used exclusively to deliver the digital product,
        send purchase confirmation, and provide customer support.
      </p>
      <h4 className="font-bold text-[var(--text-heading)] mt-4" data-id="element-136">
        3. Sharing Information
      </h4>
      <p data-id="element-137">
        We do not sell, rent, or share your personal information with third
        parties, except service providers necessary to process your purchase.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-[var(--text-muted)] text-xs uppercase tracking-widest mb-4" data-id="element-138">
        Last Updated: 2026
      </p>
      <h4 className="font-bold text-[var(--text-heading)] mt-4" data-id="element-139">1. Product</h4>
      <p data-id="element-140">
        "Sensory Feeding Victory" is a digital product. Upon purchase, you
        receive immediate access to the downloadable files.
      </p>
      <h4 className="font-bold text-[var(--text-heading)] mt-4" data-id="element-141">
        2. Personal Use
      </h4>
      <p data-id="element-142">
        The content is for personal use only. You may not redistribute, copy,
        resell, or share the material without written permission.
      </p>
      <h4 className="font-bold text-[var(--text-heading)] mt-4" data-id="element-143">
        3. Medical Disclaimer
      </h4>
      <p data-id="element-144">
        This product is educational. It does not constitute medical advice or
        therapy. Always consult with a pediatrician or qualified healthcare
        provider regarding your child's health and diet.
      </p>
      <h4 className="font-bold text-[var(--text-heading)] mt-4" data-id="element-145">
        4. Refund Policy
      </h4>
      <p data-id="element-146">
        We offer a 30-day guarantee. If you implement the method and do not see
        results, contact us within 30 days for a full refund.
      </p>
    </>;
}